import * as Immutable from 'immutable';

import { AccountStore } from './AccountReducer.types';
import { initialDataFieldEmployer, initialDataFieldEmployerFinance } from './InitialData';
import { UserDataFieldsValidation } from '../../shared/helpers/UserValidation';
import { ValidationOptions } from 'src/types';

export const setFieldValue = (data: any) => {
  if (data.value === 'false' || data.value === 'true') {
    return JSON.parse(data.value);
  }
  // semantic-ui-react v3 omits `type` from the Checkbox onChange payload (it is
  // only defaulted internally), so `data.type === 'checkbox'` no longer holds.
  // Detect a checkbox by the presence of `checked` with no `value` instead.
  const isCheckbox = data.type === 'checkbox' || (data.checked !== undefined && data.value === undefined);
  return isCheckbox ? data.checked : data.value;
};
// Fields that when validated should also validate other field
const fieldsWithDependencies = Immutable.List([
  'password',
  'serviceEngagement',
  'exposureStatus',
  'allowSMS',
  'gpa',
  'country',
  'university',
]);

// Field that will be also validate if the field key of this Object is been validated
export const FieldsHaveWithDependencies = {
  password: ['confirmation'],
  gpa: ['maxGPA'],
  serviceEngagement: ['employerServices'],
  exposureStatus: ['exposureDetails'],
  country: ['agreeCompetition'],
  university: ['agreeCompetition'],
};

interface AccountFieldData {
  userValidation: UserDataFieldsValidation;
  field: string;
  value: string | number | boolean;
  accountState: any;
  validationOptions?: ValidationOptions;
  options?: any;
}

export const setAccountFieldData = ({ userValidation, field, value, accountState, validationOptions, options }: AccountFieldData) => {
  const fieldData = {
    value,
    ...userValidation.validField({
      field: field,
      value: value,
      state: accountState,
      validationOptions: validationOptions ?? {},
      options: options,
    }),
  };
  let data: any = { [field]: fieldData };
  if (fieldsWithDependencies.includes(field)) {
    const fieldsDependencies: any = FieldsHaveWithDependencies;
    const fieldsToCheck = fieldsDependencies[field];
    const newFieldsChecked: any = {};
    if (fieldsToCheck) {
      fieldsToCheck.map((fieldToCheck: any) => {
        const fieldToCheckValue = accountState.getIn([fieldToCheck, 'value']);
        const newData = accountState.merge(Immutable.fromJS({ [field]: Immutable.fromJS(fieldData) }));

        newFieldsChecked[fieldToCheck] = {
          ...{ value: fieldToCheckValue },
          ...userValidation.validField({
            field: fieldToCheck,
            value: fieldToCheckValue,
            state: newData,
            validationOptions: validationOptions ?? {},
            options: options,
          }),
        };
      });
    }
    data = { ...data, ...newFieldsChecked };
  }

  return data;
};

export const setAccountEmailData = (userValidation: any, field: string, value: string | number | boolean) => {
  const fieldData = { value, ...userValidation.validBlurEmail(value) };

  return { [field]: fieldData };
};

export const getNewDataFields = (data: any, state: AccountStore, userValidation: any) => {
  const field = data.currentTarget ? data.currentTarget.name : data.name;
  const value = data.currentTarget ? data.currentTarget.value : setFieldValue(data);
  const dataFields = state.get('dataFields');
  let newDataFields = dataFields;

  if (state.get('accountType') === 'createForm') {
    const options = Immutable.fromJS({
      competition: state.get('competition'),
      countriesOptions: state.get('countryOptionsMap'),
    });
    newDataFields = setAccountFieldData({
      userValidation: userValidation,
      field: field,
      value: value,
      accountState: state.get('dataFields'),
      validationOptions: {},
      options: options,
    });
  } else {
    newDataFields = data.currentTarget
      ? setAccountEmailData(userValidation, field, value)
      : setAccountFieldData({
          userValidation: userValidation,
          field: field,
          value: value,
          accountState: state.get('dataFields'),
          validationOptions: { isConsultant: data.isConsultant },
        });
  }

  const dataFieldValue = newDataFields[field].value;
  if (field === 'presently_employed' && dataFieldValue === false) {
    return state.merge(
      Immutable.fromJS({
        dataFields: dataFields.merge(
          Immutable.fromJS({
            ...newDataFields,
            ...initialDataFieldEmployer,
            ...initialDataFieldEmployerFinance,
          })
        ),
      })
    );
  } else if (field === 'finance' && dataFieldValue === false && state.getIn(['dataFields', 'presently_employed', 'value']) === true) {
    return state.merge(
      Immutable.fromJS({
        dataFields: dataFields.merge(
          Immutable.fromJS({
            ...newDataFields,
            ...initialDataFieldEmployerFinance,
          })
        ),
      })
    );
  } else {
    return state.merge(Immutable.fromJS({ dataFields: dataFields.merge(Immutable.fromJS(newDataFields)) }));
  }
};

export const getNewState = (data: any, state: AccountStore, userValidation: any) => {
  const newState = getNewDataFields(data, state, userValidation);
  const account = `${state.get('accountType')}Form`;
  const currentForm = state.get('currentForm');
  const form = state.getIn([account, currentForm]);
  if (form) {
    // @ts-expect-error ImmutableJS get returns unknown
    const fields = form.get('fields');

    return newState.setIn([account, currentForm, 'valid'], userValidation.createValidation(newState.get('dataFields'), fields));
  }

  return newState;
};

export const reduceObjectMap = (currentObject: any, result: any = {}) => {
  Object.keys(currentObject).map((key: string) => {
    const currentValue = currentObject[key];
    if (currentValue === null || currentValue === undefined) {
      result[key] = currentValue;
    } else if (Array.isArray(currentValue)) {
      result[key] = currentValue.slice();
    } else if (typeof currentValue === 'object') {
      reduceObjectMap(currentValue, result);
    } else {
      result[key] = currentValue;
    }
  });

  return result;
};
